import React, { useState, useEffect, useRef } from 'react';
import BlockedMonthDateInput from './BlockedMonthDateInput';
import { isMonthBlocked, isBeforeEarliestBookable } from '../../utils/tourDateValidation';
import '../../css/common/FormComponent.css';

const FormComponent = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  formConfig, 
  initialData = {}, 
  maxWidth = '700px',
  inline = false,
}) => {
  const [formData, setFormData] = useState(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const previousIsOpenRef = useRef(false);
  const initialDataRef = useRef(initialData);

  // Reset form data only when component opens (not when it's already open)
  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      setFormData(initialDataRef.current);
      setSubmitStatus(null);
      setFieldErrors({});
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen]);

  // Update the ref when initialData changes
  useEffect(() => {
    initialDataRef.current = initialData;
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleCheckboxChange = (e, fieldName) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const currentValues = Array.isArray(prev[fieldName]) ? prev[fieldName] : [];
      if (checked) {
        return {
          ...prev,
          [fieldName]: [...currentValues, value]
        };
      } else {
        return {
          ...prev,
          [fieldName]: currentValues.filter(v => v !== value)
        };
      }
    });
  };

  const validateDateFields = () => {
    const errors = {};
    (formConfig.fields || []).forEach((field) => {
      if (field.type !== 'date') return;
      const value = formData[field.name];
      if (!value) return;

      if (field.blockedMonths?.length && isMonthBlocked(value, field.blockedMonths)) {
        errors[field.name] =
          field.blockedMonthMessage ||
          field.helpText ||
          'This date is not available. Please choose another date.';
        return;
      }

      if (
        field.minBufferBusinessDays &&
        isBeforeEarliestBookable(value, field.minBufferBusinessDays)
      ) {
        errors[field.name] = 'Please choose a later date.';
      }
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateDateFields()) {
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await onSubmit(formData);
      setSubmitStatus(result);
      
      if (result.success) {
        setFormData(initialDataRef.current);
        if (onClose && !inline) {
          setTimeout(() => {
            onClose();
          }, 3000);
        }
      } else {
        console.error('FormComponent: Form submission failed:', result.message);
      }
    } catch (error) {
      console.error('FormComponent: Unexpected error during form submission:', error);
      setSubmitStatus({
        success: false,
        message: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      id: field.name,
      name: field.name,
      value: formData[field.name] !== undefined ? formData[field.name] : '',
      onChange: handleInputChange,
      required: field.required,
      placeholder: field.placeholder,
      className: 'form-input'
    };
    
    // Helper to resolve options (supports both arrays and functions)
    const getOptions = (field) => {
      return typeof field.options === 'function'
        ? field.options(formData)
        : field.options;
    };

    switch (field.type) {
      case 'email':
        return <input type="email" {...commonProps} />;
      
      case 'tel':
        return <input type="tel" {...commonProps} />;
      
      case 'date':
        if (field.blockedMonths?.length || field.minBufferBusinessDays) {
          return (
            <BlockedMonthDateInput
              id={field.name}
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleInputChange}
              required={field.required}
              blockedMonths={field.blockedMonths || []}
              minBufferBusinessDays={field.minBufferBusinessDays || 0}
              min={field.min || new Date().toISOString().split('T')[0]}
              className="form-input"
            />
          );
        }
        return (
          <input
            type="date"
            {...commonProps}
            min={field.min || new Date().toISOString().split('T')[0]}
          />
        );
      
      case 'number':
        return <input 
          type="number" 
          {...commonProps} 
          min={field.min || 1}
        />;
      
      case 'select':
        const selectOptions = getOptions(field);
        return (
          <select {...commonProps}>
            <option value="">{field.placeholder || 'Select an option'}</option>
            {selectOptions?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'textarea':
        return (
          <textarea 
            {...commonProps} 
            rows={field.rows || 3}
          />
        );
      
      case 'checkbox':
        const selectedValues = Array.isArray(formData[field.name]) ? formData[field.name] : [];
        const checkboxOptions = getOptions(field);
        return (
          <div className="checkbox-group">
            {checkboxOptions?.map(option => (
              <label key={option} className="checkbox-label">
                <input
                  type="checkbox"
                  name={field.name}
                  value={option}
                  checked={selectedValues.includes(option)}
                  onChange={(e) => handleCheckboxChange(e, field.name)}
                  className="checkbox-input"
                />
                <span className="checkbox-text">{option}</span>
              </label>
            ))}
          </div>
        );
      
      default:
        return <input type="text" {...commonProps} />;
    }
  };

  const renderFormFields = () => {
    const fields = formConfig.fields;
    const renderedFields = [];

    const renderFieldError = (fieldName) =>
      fieldErrors[fieldName] ? (
        <p className="field-error-text" role="alert">{fieldErrors[fieldName]}</p>
      ) : null;
    
    // Helper to resolve label (supports both strings and functions)
    const getLabel = (field) => {
      return typeof field.label === 'function' 
        ? field.label(formData) 
        : field.label;
    };
    
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      const nextField = fields[i + 1];
      
      // Check if field should be shown based on conditional logic
      if (field.conditional) {
        const { field: conditionalField, value, values, includes, showWhen = true } = field.conditional;
        let shouldShow = true;
        
        if (includes) {
          // Check if array includes value (for checkboxes) - case-insensitive substring match
          const fieldData = formData[conditionalField];
          shouldShow = Array.isArray(fieldData) && fieldData.some(val => 
            val.toLowerCase().includes(includes.toLowerCase())
          );
        } else if (values) {
          // Check if value is in array of possible values
          shouldShow = values.includes(formData[conditionalField]);
        } else if (value !== undefined) {
          // Check for exact value match
          shouldShow = showWhen ? 
            formData[conditionalField] === value : 
            formData[conditionalField] !== value;
        }
        
        if (!shouldShow) {
          continue; // Skip this field
        }
      }
      
      if (field.fullWidth) {
        // Full width field - render alone
        renderedFields.push(
          <div key={field.name} className="form-group full-width">
            <label htmlFor={field.name}>
              {field.icon} {getLabel(field)} {field.required && '*'}
            </label>
            {field.helpText && <p className="field-help-text">{field.helpText}</p>}
            {renderField(field)}
            {renderFieldError(field.name)}
          </div>
        );
      } else if (nextField && !nextField.fullWidth && !field.fullWidth) {
        // Check if next field should be shown
        let nextFieldShouldShow = true;
        if (nextField.conditional) {
          const { field: conditionalField, value, values, includes, showWhen = true } = nextField.conditional;
          
          if (includes) {
            const fieldData = formData[conditionalField];
            nextFieldShouldShow = Array.isArray(fieldData) && fieldData.some(val => 
              val.toLowerCase().includes(includes.toLowerCase())
            );
          } else if (values) {
            nextFieldShouldShow = values.includes(formData[conditionalField]);
          } else if (value !== undefined) {
            nextFieldShouldShow = showWhen ? 
              formData[conditionalField] === value : 
              formData[conditionalField] !== value;
          }
        }
        
        if (nextFieldShouldShow) {
          // Two fields in a row
          renderedFields.push(
            <div key={`row-${i}`} className="form-row">
              <div className="form-group">
                <label htmlFor={field.name}>
                  {field.icon} {getLabel(field)} {field.required && '*'}
                </label>
                {field.helpText && <p className="field-help-text">{field.helpText}</p>}
                {renderField(field)}
                {renderFieldError(field.name)}
              </div>
              <div className="form-group">
                <label htmlFor={nextField.name}>
                  {nextField.icon} {getLabel(nextField)} {nextField.required && '*'}
                </label>
                {nextField.helpText && <p className="field-help-text">{nextField.helpText}</p>}
                {renderField(nextField)}
                {renderFieldError(nextField.name)}
              </div>
            </div>
          );
          i++; // Skip the next field since we already rendered it
        } else {
          // Single field in a row (next field is hidden)
          renderedFields.push(
            <div key={field.name} className="form-group">
              <label htmlFor={field.name}>
                {field.icon} {getLabel(field)} {field.required && '*'}
              </label>
              {field.helpText && <p className="field-help-text">{field.helpText}</p>}
              {renderField(field)}
              {renderFieldError(field.name)}
            </div>
          );
        }
      } else {
        // Single field in a row
        renderedFields.push(
          <div key={field.name} className="form-group">
            <label htmlFor={field.name}>
              {field.icon} {getLabel(field)} {field.required && '*'}
            </label>
            {field.helpText && <p className="field-help-text">{field.helpText}</p>}
            {renderField(field)}
            {renderFieldError(field.name)}
          </div>
        );
      }
    }
    
    return renderedFields;
  };

  if (!isOpen) return null;

  const formBody = (
    <div 
      className={inline ? 'form-component-content form-component-inline' : 'form-component-content'}
      onClick={(e) => inline ? undefined : e.stopPropagation()}
      style={{ maxWidth }}
    >
      {!inline && (
        <button type="button" className="form-component-close" onClick={onClose}>×</button>
      )}
      
      <div className="form-component-header">
        {!inline && (
          <div className="form-component-icon">
            {formConfig.icon}
          </div>
        )}
        <h2>{formConfig.title}</h2>
        <p>{formConfig.subtitle}</p>
      </div>

      {formConfig.seasonalNotice && (
        <div className="form-component-seasonal-notice" role="note">
          {formConfig.seasonalNotice}
        </div>
      )}

      {submitStatus && (
        <div className={`form-component-message ${submitStatus.success ? 'success' : 'error'}`}>
          {submitStatus.message}
        </div>
      )}

        <form onSubmit={handleSubmit} className="form-component-form">
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            className="form-honeypot"
            aria-hidden="true"
          />
          {renderFormFields()}

        {formConfig.note && (
          <div className="form-component-note">
            <p><strong>Note:</strong> {formConfig.note}</p>
          </div>
        )}

        <div className="form-component-actions">
          {!inline && (
            <button
              type="button"
              onClick={onClose}
              className="form-component-cancel"
              disabled={isSubmitting}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="form-component-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : formConfig.submitText}
          </button>
        </div>
      </form>
    </div>
  );

  if (inline) {
    return <div className="form-overlay">{formBody}</div>;
  }

  return (
    <div
      className="form-component-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {formBody}
    </div>
  );
};

export default FormComponent; 